import React, { useState, useRef, useEffect, useCallback } from "react";
import { filterCitiesWithTamilSupport } from "../helpers/citySearchHelper";

/**
 * TalukSearchSelect
 * A searchable dropdown for taluk/area selection with bilingual support.
 * Supports both English and Tamil search with arrow key navigation.
 *
 * Props:
 *   options     – array of taluk/area names
 *   value       – currently selected value
 *   onChange    – (taluk: string) => void
 *   onBlur      – () => void
 *   disabled    – boolean
 *   className   – class string for the input element
 *   placeholder – string
 *   autoFocus   – if true, focus input on mount
 */
const TalukSearchSelect = ({
  options = [],
  value = "",
  onChange,
  onBlur,
  disabled = false,
  className = "",
  placeholder = "Select taluk / area…",
  autoFocus = false,
}) => {
  const [query, setQuery] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const tamilCacheRef = useRef(new Map());
  const debounceTimeoutRef = useRef(null);
  const lastSearchStateRef = useRef({ query: null, options: null });

  // Sync display text when external value changes
  useEffect(() => {
    setQuery(value || "");
    setHighlightedIndex(-1);
  }, [value]);

  useEffect(() => {
    if (autoFocus && !disabled) {
      inputRef.current?.focus();
    }
  }, [autoFocus, disabled]);

  // Filter options with bilingual support
  const performSearch = useCallback(async (searchQuery) => {
    const previous = lastSearchStateRef.current;
    if (searchQuery === previous.query && options === previous.options) {
      return;
    }
    lastSearchStateRef.current = { query: searchQuery, options };

    if (searchQuery.trim().length === 0) {
      setFiltered(options);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await filterCitiesWithTamilSupport(
        options,
        searchQuery,
        tamilCacheRef.current,
        25
      );
      setFiltered(results);
    } catch (error) {
      console.error("Search error:", error);
      // Fallback to simple English search
      setFiltered(
        options
          .filter((t) => t.toLowerCase().includes(searchQuery.toLowerCase()))
          .slice(0, 25)
      );
    } finally {
      setIsSearching(false);
    }
  }, [options]);

  // Debounced search
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      performSearch(query);
      setHighlightedIndex(-1);
    }, 150);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [query, performSearch]);

  useEffect(() => {
    performSearch(query);
  }, [performSearch, options]);

  // Close dropdown on outside click; revert if user typed but didn't pick
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (!options.includes(query)) {
          setQuery(value || "");
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [query, value, options]);

  // Scroll highlighted row into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll("li[data-item]");
      items[highlightedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const handleSelect = (taluk) => {
    setQuery(taluk);
    onChange(taluk);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setIsOpen(true);
    setHighlightedIndex(-1);
    if (val === "") onChange("");
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
          handleSelect(filtered[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
          setIsOpen(true);
          performSearch(query);
        }}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`${className} no-translate`}
          autoComplete="off"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
        />
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-blue-100 bg-white py-1 shadow-xl text-sm"
        >
          {isSearching ? (
            <li className="px-3.5 py-2.5 text-slate-400 italic flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full bg-blue-400 animate-pulse" />
              Searching...
            </li>
          ) : filtered.length > 0 ? (
            filtered.map((taluk, i) => (
              <li
                key={taluk}
                data-item
                role="option"
                aria-selected={i === highlightedIndex}
                onMouseDown={() => handleSelect(taluk)}
                className={`cursor-pointer px-3.5 py-2 font-medium transition-colors ${
                  i === highlightedIndex
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-800 hover:bg-slate-50"
                }`}
              >
                {taluk}
              </li>
            ))
          ) : (
            <li className="px-3.5 py-2.5 text-slate-400 italic">
              {query.trim().length === 0
                ? "Type to search... (English or Tamil)"
                : "No matches found"}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default TalukSearchSelect;
